import { put, select, takeLatest } from 'redux-saga/effects'

import { callApi } from '@/shared/tools/apiSagaHelperV2'
import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import {
  addOfferCutPeriod,
  removeOfferCutPeriod,
  updateOfferCutPeriod,
} from '@/containers/OrganizationsManagement/organizationsManagementSlice'

export const offerCutPeriodsActions = {
  CREATE: 'OFFERS/CUT_PERIODS/CREATE',
  UPDATE: 'OFFERS/CUT_PERIODS/UPDATE',
  DELETE: 'OFFERS/CUT_PERIODS/DELETE',
}

interface ExtraRoutes {
  offerCutPeriods?: {
    list?: string
    create?: string
    update?: string
    delete?: string
  }
}

// CREATE supports `team_ids: number[]` (multi-team) so the backend can spawn
// one cut period per selected team in a single request. UPDATE keeps the
// singular `team_id` shape ─ an existing cut period maps to exactly one team.
interface CutPeriodCreatePayload {
  id?: number | string
  offer_id?: number | string
  team_ids: Array<number | string>
  cut_start: string
  cut_end?: string | null
}

interface CutPeriodUpdatePayload {
  id?: number | string
  offer_id?: number | string
  team_id: number | string
  cut_start: string
  cut_end?: string | null
}

interface CutPeriodResponse {
  id?: number | string
  offer_id?: number | string
  team_id?: number | string
  cut_start?: string
  cut_end?: string | null
}

interface CreateAction {
  payload: CutPeriodCreatePayload
  enqueue?: any
  onSuccess?: () => void
  onError?: (error: unknown) => void
}

interface UpdateAction {
  cutPeriodId: number | string
  payload: CutPeriodUpdatePayload
  enqueue?: any
  onSuccess?: () => void
  onError?: (error: unknown) => void
}

interface DeleteAction {
  cutPeriodId: number | string
  enqueue?: any
  onSuccess?: () => void
  onError?: (error: unknown) => void
}

/**
 * Backend response shape on CREATE — `callApi` already strips the outer
 * `{ success, data, meta }` wrapper, so the saga receives the inner object:
 *
 *   { periods: [ { id, offer_id, team_id, cut_start, cut_end }, ... ] }
 *
 * One period record per team_id that was just created, fully populated with
 * its backend id, so edit/delete work right away.
 */
interface CutPeriodCreateResponse {
  periods: CutPeriodResponse[]
}

function* createOfferCutPeriodSaga(action: CreateAction): Generator {
  const { enqueue, onSuccess, onError } = action
  try {
    const { offerCutPeriods } = (yield select(selectExtraRoutesAuthData)) as ExtraRoutes
    if (!offerCutPeriods?.create) {
      // No endpoint configured: signal failure so the modal unlocks instead
      // of hanging in submitting state.
      if (onError) onError(new Error('offerCutPeriods.create route missing'))
      return
    }

    const data = (yield callApi({
      endpoint: offerCutPeriods.create,
      method: 'POST',
      payload: action.payload as any,
      enqueue,
      showErrorSnackbar: true,
    })) as CutPeriodCreateResponse | undefined

    // Append every period returned by the backend ─ the slice keeps any
    // previously persisted periods intact.
    if (data && Array.isArray(data.periods)) {
      for (const period of data.periods) {
        yield put(addOfferCutPeriod(period))
      }
    }

    // POST returned 2xx ─ always call onSuccess so the modal closes and the
    // submit button is re-enabled, even if the response body was empty or
    // didn't match the expected shape.
    if (onSuccess) onSuccess()
  } catch (error) {
    if (onError) onError(error)
  }
}

function* updateOfferCutPeriodSaga(action: UpdateAction): Generator {
  const { cutPeriodId, payload, enqueue, onSuccess, onError } = action
  try {
    const { offerCutPeriods } = (yield select(selectExtraRoutesAuthData)) as ExtraRoutes
    if (!offerCutPeriods?.update) return

    const data = (yield callApi({
      endpoint: offerCutPeriods.update,
      method: 'PUT',
      payload: payload as any,
      asteriskReplacement: String(cutPeriodId) as any,
      enqueue,
      showErrorSnackbar: true,
    })) as CutPeriodResponse | undefined

    if (data) {
      yield put(updateOfferCutPeriod(data))
      if (onSuccess) onSuccess()
    }
  } catch (error) {
    if (onError) onError(error)
  }
}

function* deleteOfferCutPeriodSaga(action: DeleteAction): Generator {
  const { cutPeriodId, enqueue, onSuccess, onError } = action
  try {
    const { offerCutPeriods } = (yield select(selectExtraRoutesAuthData)) as ExtraRoutes
    if (!offerCutPeriods?.delete) return

    yield callApi({
      endpoint: offerCutPeriods.delete,
      method: 'DELETE',
      asteriskReplacement: String(cutPeriodId) as any,
      enqueue,
      showErrorSnackbar: true,
    })

    yield put(removeOfferCutPeriod(cutPeriodId))
    if (onSuccess) onSuccess()
  } catch (error) {
    if (onError) onError(error)
  }
}

export default function* offerCutPeriodsSagas() {
  yield takeLatest(offerCutPeriodsActions.CREATE, createOfferCutPeriodSaga as any)
  yield takeLatest(offerCutPeriodsActions.UPDATE, updateOfferCutPeriodSaga as any)
  yield takeLatest(offerCutPeriodsActions.DELETE, deleteOfferCutPeriodSaga as any)
}
